import { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Filter, FunnelX, User, FileText, Phone, AlertTriangle, Users, UserCheck, Baby, UserX, MoreHorizontal } from 'lucide-react';
import { Select } from '../components/ui/Select';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { miembroService, MiembroFilters, PaginatedMiembrosResponse } from '../services/miembroService';
import { Miembro } from '../lib/supabase';
import { MiembroModal } from '../components/MiembroModal';
import { MiembroFichaModal } from '../components/MiembroFichaModal';
import { SoftDeleteMiembroModal } from '../components/SoftDeleteMiembroModal';
import { HardDeleteMiembroModal } from '../components/HardDeleteMiembroModal';
import { MiembrosSinDerechosModal } from '../components/MiembrosSinDerechosModal';
import { tieneCamposIncompletos, contarCamposIncompletos } from '../utils/miembroValidation';
import { EstadoRegistroBadge } from '../components/EstadoRegistroBadge';

export const DetalleMiembrosPage: React.FC = () => {
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const [response, setResponse] = useState<PaginatedMiembrosResponse>({
    data: [],
    total: 0,
    totalAdultos: 0,
    totalNinos: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedMiembro, setSelectedMiembro] = useState<Miembro | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showFichaModal, setShowFichaModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [showFilters, setShowFilters] = useState(true);
  const [showSoftDeleteModal, setShowSoftDeleteModal] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [showSinDerechosModal, setShowSinDerechosModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const [busqueda, setBusqueda] = useState('');
  const [tipoMiembro, setTipoMiembro] = useState<'adulto' | 'niño' | ''>('');
  const [misioneroNacional, setMisioneroNacional] = useState<boolean | ''>('');
  const [instructorLocal, setInstructorLocal] = useState<boolean | ''>('');
  const [cargo, setCargo] = useState('');
  const [estadoFichaFilter, setEstadoFichaFilter] = useState('');
  const [fichaStats, setFichaStats] = useState({
    total_por_solicitar: 0,
    total_solicitado: 0,
    total_link_vencido: 0,
    total_prerregistrado: 0,
    total_registrado: 0,
    total_foto_pendiente: 0,
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  const buildFilters = (): MiembroFilters => {
    const filters: MiembroFilters = {};

    if (busqueda.trim()) filters.busqueda = busqueda.trim();
    if (tipoMiembro) filters.tipoMiembro = tipoMiembro as 'adulto' | 'niño';
    if (misioneroNacional !== '') filters.misioneroNacional = misioneroNacional as boolean;
    if (instructorLocal !== '') filters.instructorLocal = instructorLocal as boolean;
    if (cargo) filters.cargo = cargo;
    if (estadoFichaFilter) filters.estadoFicha = estadoFichaFilter;

    return filters;
  };

  const loadMiembros = async (page: number = 1) => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      const filters = buildFilters();
      const [data, stats] = await Promise.all([
        miembroService.getPaginated(selectedLumisial.id, page, 20, filters),
        miembroService.getEstadoFichaStats(selectedLumisial.id),
      ]);
      setResponse(data);
      setFichaStats(stats);
    } catch (error) {
      console.error('Error loading miembros:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedLumisial) return;
    const timeout = setTimeout(() => {
      loadMiembros(1);
    }, 0);
    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLumisial]);

  // Efecto reactivo: recarga automáticamente cuando cambia cualquier filtro
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!selectedLumisial) return;

    // Debounce para búsqueda por texto, inmediato para selects
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadMiembros(1);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, tipoMiembro, misioneroNacional, instructorLocal, cargo, estadoFichaFilter]);


  const handleCreate = () => {
    setSelectedMiembro(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewFicha = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setShowFichaModal(true);
  };

  const handleSoftDelete = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setShowSoftDeleteModal(true);
  };

  const handleConfirmSoftDelete = async (motivo: string) => {
    if (!selectedMiembro || !usuario) return;

    try {
      await miembroService.softDelete(selectedMiembro.id, motivo, usuario.id);
      loadMiembros(response.page);
    } catch (error) {
      console.error('Error al marcar miembro sin derechos:', error);
      throw error;
    }
  };

  const handleHardDelete = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setShowHardDeleteModal(true);
  };

  const handleConfirmHardDelete = async () => {
    if (!selectedMiembro) return;

    try {
      await miembroService.hardDelete(selectedMiembro.id);
      loadMiembros(response.page);
    } catch (error) {
      console.error('Error al eliminar miembro:', error);
      throw error;
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedMiembro(null);
  };

  const handleModalSuccess = () => {
    loadMiembros(response.page);
  };

  const resetFilters = () => {
    setBusqueda('');
    setTipoMiembro('');
    setMisioneroNacional('');
    setInstructorLocal('');
    setCargo('');
    setEstadoFichaFilter('');
  };

  // Click en un contador de estado: alterna el filtro por ese estado.
  const toggleEstadoFilter = (estado: string) => {
    setEstadoFichaFilter((prev) => (prev === estado ? '' : estado));
  };

  const estadoChipClass = (estado: string, text: string, hover: string, ring: string) =>
    `rounded-full px-2 py-0.5 transition-colors ${text} ${hover} ${estadoFichaFilter === estado ? ring : ''}`;

  const hasActiveFilters = !!(busqueda || tipoMiembro || misioneroNacional !== '' || instructorLocal !== '' || cargo || estadoFichaFilter);

  const totalMiembros = response.total;
  const totalAdultos = response.totalAdultos;
  const totalNinos = response.totalNinos;

  let filtrosBtnColorClass = 'bg-slate-100 text-slate-700 hover:bg-slate-200';
  if (hasActiveFilters) {
    filtrosBtnColorClass = 'bg-blue-600 text-white';
  } else if (showFilters) {
    filtrosBtnColorClass = 'bg-blue-100 text-blue-700';
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <h2 className="text-3xl font-bold text-slate-800">Gestión de Miembros</h2>
          <div className="flex items-center gap-3 sm:pl-6 sm:border-l-2 sm:border-slate-200">
            <div className="group relative flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg cursor-default">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">{totalMiembros}</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Miembros
              </div>
            </div>
            <div className="group relative flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg cursor-default">
              <UserCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-700">{totalAdultos}</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Adultos
              </div>
            </div>
            <div className="group relative flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg cursor-default">
              <Baby className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-semibold text-slate-700">{totalNinos}</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Niños
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors self-start sm:self-auto"
        >
          <Plus className="w-5 h-5" />
          Nuevo Miembro
        </button>
      </div>

      {/* Indicadores de estado de registro — cada uno filtra la lista al hacer clic */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <button
          type="button"
          aria-pressed={estadoFichaFilter === 'por_solicitar'}
          onClick={() => toggleEstadoFilter('por_solicitar')}
          className={estadoChipClass('por_solicitar', 'text-slate-600', 'hover:bg-slate-100', 'ring-2 ring-offset-1 ring-slate-400')}
        >
          <strong>{fichaStats.total_por_solicitar}</strong> por solicitar
        </button>
        {fichaStats.total_solicitado > 0 && (
          <button
            type="button"
            aria-pressed={estadoFichaFilter === 'solicitado'}
            onClick={() => toggleEstadoFilter('solicitado')}
            className={estadoChipClass('solicitado', 'text-blue-600 font-medium', 'hover:bg-blue-50', 'ring-2 ring-offset-1 ring-blue-400')}
          >
            {fichaStats.total_solicitado} solicitado{fichaStats.total_solicitado === 1 ? '' : 's'}
          </button>
        )}
        {fichaStats.total_link_vencido > 0 && (
          <button
            type="button"
            aria-pressed={estadoFichaFilter === 'link_vencido'}
            onClick={() => toggleEstadoFilter('link_vencido')}
            className={estadoChipClass('link_vencido', 'text-red-600 font-medium', 'hover:bg-red-50', 'ring-2 ring-offset-1 ring-red-400')}
          >
            {fichaStats.total_link_vencido} link vencido
          </button>
        )}
        {fichaStats.total_foto_pendiente > 0 && (
          <button
            type="button"
            aria-pressed={estadoFichaFilter === 'foto_pendiente'}
            onClick={() => toggleEstadoFilter('foto_pendiente')}
            className={estadoChipClass('foto_pendiente', 'text-amber-700 font-medium', 'hover:bg-amber-50', 'ring-2 ring-offset-1 ring-amber-400')}
          >
            {fichaStats.total_foto_pendiente} foto{fichaStats.total_foto_pendiente === 1 ? '' : 's'} pendiente{fichaStats.total_foto_pendiente === 1 ? '' : 's'}
          </button>
        )}
        <button
          type="button"
          aria-pressed={estadoFichaFilter === 'prerregistrado'}
          onClick={() => toggleEstadoFilter('prerregistrado')}
          className={`rounded-full px-2 py-0.5 transition-colors font-medium ${fichaStats.total_prerregistrado > 0 ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'text-slate-600 hover:bg-slate-100'} ${estadoFichaFilter === 'prerregistrado' ? 'ring-2 ring-offset-1 ring-orange-400' : ''}`}
        >
          {fichaStats.total_prerregistrado > 0
            ? `${fichaStats.total_prerregistrado} prerregistrados`
            : <><strong>{fichaStats.total_prerregistrado}</strong> prerregistrados</>}
        </button>
        <button
          type="button"
          aria-pressed={estadoFichaFilter === 'registrado'}
          onClick={() => toggleEstadoFilter('registrado')}
          className={estadoChipClass('registrado', 'text-slate-600', 'hover:bg-slate-100', 'ring-2 ring-offset-1 ring-slate-400')}
        >
          <strong>{fichaStats.total_registrado}</strong> registrados
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, RUT o email..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
          <div className="flex items-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 transition-colors font-semibold ${filtrosBtnColorClass} ${hasActiveFilters ? 'rounded-l-lg' : 'rounded-lg'}`}
            >
              <Filter className="w-5 h-5" />
              Filtros
              {hasActiveFilters && <span className="ml-2 bg-white/30 px-2 py-1 rounded text-xs">Activos</span>}
            </button>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="px-3 py-3 bg-red-500 text-white hover:bg-red-600 rounded-r-lg transition-colors border-l border-red-400"
                title="Limpiar todos los filtros"
              >
                <FunnelX className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                label="Edad"
                options={[
                  { value: 'adulto', label: 'Adulto' },
                  { value: 'niño', label: 'Niño' },
                ]}
                value={tipoMiembro}
                onChange={(v) => setTipoMiembro(v as 'adulto' | 'niño' | '')}
                placeholder="Todos"
              />

              <Select
                label="Cargo"
                options={[
                  { value: 'Ungido', label: 'Ungido' },
                  { value: 'Administración', label: 'Administración' },
                ]}
                value={cargo}
                onChange={(v) => setCargo(v)}
                placeholder="Todos"
              />

              <Select
                label="Misionero Nacional"
                options={[
                  { value: 'true', label: 'Sí' },
                  { value: 'false', label: 'No' },
                ]}
                value={misioneroNacional === '' ? '' : String(misioneroNacional)}
                onChange={(v) => setMisioneroNacional(v === '' ? '' : v === 'true')}
                placeholder="Todos"
              />

              <Select
                label="Instructor Local"
                options={[
                  { value: 'true', label: 'Sí' },
                  { value: 'false', label: 'No' },
                ]}
                value={instructorLocal === '' ? '' : String(instructorLocal)}
                onChange={(v) => setInstructorLocal(v === '' ? '' : v === 'true')}
                placeholder="Todos"
              />

              <Select
                label="Estado de registro"
                options={[
                  { value: 'por_solicitar', label: 'Por solicitar' },
                  { value: 'solicitado', label: 'Solicitado' },
                  { value: 'link_vencido', label: 'Link vencido' },
                  { value: 'foto_pendiente', label: 'Con foto pendiente' },
                  { value: 'prerregistrado', label: 'Prerregistrado' },
                  { value: 'registrado', label: 'Registrado' },
                ]}
                value={estadoFichaFilter}
                onChange={setEstadoFichaFilter}
                placeholder="Todos"
              />
            </div>

          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700" />
          </div>
        )}
        {!loading && response.data.length === 0 && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">No hay miembros registrados</p>
            {hasActiveFilters && <p className="text-sm text-slate-500">Intenta cambiar los filtros</p>}
            {!hasActiveFilters && (
              <button
                onClick={handleCreate}
                className="mt-4 text-blue-600 hover:underline font-medium"
              >
                Registra el primer miembro
              </button>
            )}
          </div>
        )}
        {!loading && response.data.length > 0 && (
          <>
            {/* Fichas — mobile */}
            <div className="md:hidden space-y-3">
              {response.data.map((miembro) => {
                const tieneIncompletos = tieneCamposIncompletos(miembro);
                const cantidadIncompletos = contarCamposIncompletos(miembro);
                let deleteActions: React.ReactNode = null;
                if (usuario?.rol.nombre === 'admin_global') {
                  deleteActions = (
                    <>
                      <button
                        onClick={() => { handleSoftDelete(miembro); setOpenDropdownId(null); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-amber-600"
                      >
                        <UserX className="w-4 h-4" />
                        <span>Sin derechos</span>
                      </button>
                      <button
                        onClick={() => { handleHardDelete(miembro); setOpenDropdownId(null); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Eliminar</span>
                      </button>
                    </>
                  );
                } else if (usuario?.rol.nombre.includes('admin')) {
                  deleteActions = (
                    <button
                      onClick={() => { handleSoftDelete(miembro); setOpenDropdownId(null); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-amber-600"
                    >
                      <UserX className="w-4 h-4" />
                      <span>Sin derechos</span>
                    </button>
                  );
                }
                return (
                  <div key={miembro.id} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-start gap-3">
                        {miembro.foto_url
                          ? <img src={miembro.foto_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
                          : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-0.5">
                              {miembro.nombres?.[0]}{miembro.apellidos?.[0]}
                            </div>
                        }
                        <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-800">
                            {miembro.nombres} {miembro.apellidos}
                          </p>
                          <EstadoRegistroBadge miembro={miembro} />
                          {tieneIncompletos && (
                            <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full" title={`${cantidadIncompletos} campo(s) recomendado(s) incompleto(s)`}>
                              <AlertTriangle className="w-3 h-3" />
                              {cantidadIncompletos}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                            {miembro.tipo_miembro === 'adulto' ? 'Adulto' : 'Niño'}
                          </span>
                          {miembro.telefono && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <Phone className="w-3 h-3" />
                              {miembro.telefono}
                            </span>
                          )}
                          {miembro.email && (
                            <span className="text-xs text-slate-500 truncate max-w-[160px]">{miembro.email}</span>
                          )}
                        </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleViewFicha(miembro)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Ver Ficha"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setOpenDropdownId(openDropdownId === miembro.id ? null : miembro.id)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Más acciones"
                          >
                            <MoreHorizontal className="w-4 h-4 text-slate-500" />
                          </button>
                          {openDropdownId === miembro.id && (
                            <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 min-w-[180px]">
                              <button
                                onClick={() => { handleEdit(miembro); setOpenDropdownId(null); }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-blue-600"
                              >
                                <Edit2 className="w-4 h-4" />
                                <span>Editar</span>
                              </button>
                              {deleteActions}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tabla — desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Nombre Completo</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Edad</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Teléfono</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Email</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {response.data.map((miembro) => {
                    const tieneIncompletos = tieneCamposIncompletos(miembro);
                    const cantidadIncompletos = contarCamposIncompletos(miembro);
                    let desktopDeleteActions: React.ReactNode = null;
                    if (usuario?.rol.nombre === 'admin_global') {
                      desktopDeleteActions = (
                        <>
                          <button
                            onClick={() => { handleSoftDelete(miembro); setOpenDropdownId(null); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-amber-600"
                          >
                            <UserX className="w-4 h-4" />
                            <span>Sin derechos</span>
                          </button>
                          <button
                            onClick={() => { handleHardDelete(miembro); setOpenDropdownId(null); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Eliminar</span>
                          </button>
                        </>
                      );
                    } else if (usuario?.rol.nombre.includes('admin')) {
                      desktopDeleteActions = (
                        <button
                          onClick={() => { handleSoftDelete(miembro); setOpenDropdownId(null); }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-amber-600"
                        >
                          <UserX className="w-4 h-4" />
                          <span>Sin derechos</span>
                        </button>
                      );
                    }
                    return (
                      <tr key={miembro.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">
                          <div className="flex items-center gap-2 flex-wrap">
                            {miembro.foto_url
                              ? <img src={miembro.foto_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                              : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                  {miembro.nombres?.[0]}{miembro.apellidos?.[0]}
                                </div>
                            }
                            {miembro.nombres} {miembro.apellidos}
                            <EstadoRegistroBadge miembro={miembro} />
                            {tieneIncompletos && (
                              <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full" title={`${cantidadIncompletos} campo(s) recomendado(s) incompleto(s)`}>
                                <AlertTriangle className="w-3 h-3" />
                                {cantidadIncompletos}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1.5 rounded-full font-medium">
                            {miembro.tipo_miembro === 'adulto' ? 'Adulto' : 'Niño'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {miembro.telefono ? (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              {miembro.telefono}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                          {miembro.email || '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleViewFicha(miembro)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Ver Ficha"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setOpenDropdownId(openDropdownId === miembro.id ? null : miembro.id)}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Más acciones"
                              >
                                <MoreHorizontal className="w-4 h-4 text-slate-500" />
                              </button>
                              {openDropdownId === miembro.id && (
                                <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 min-w-[180px]">
                                  <button
                                    onClick={() => { handleEdit(miembro); setOpenDropdownId(null); }}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-blue-600"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    <span>Editar</span>
                                  </button>
                                  {desktopDeleteActions}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {response.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Página {response.page} de {response.totalPages} ({response.total} miembros)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadMiembros(response.page - 1)}
                disabled={response.page === 1}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>
              <button
                onClick={() => loadMiembros(response.page + 1)}
                disabled={response.page === response.totalPages}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {usuario?.rol.nombre.includes('admin') && (
        <div className="text-center mt-4">
          <button
            onClick={() => setShowSinDerechosModal(true)}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline"
          >
            Ver miembros sin derechos
          </button>
        </div>
      )}

      <MiembroModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        miembro={selectedMiembro || undefined}
        lumisialId={selectedLumisial?.id || ''}
        mode={modalMode}
      />

      <MiembroFichaModal
        isOpen={showFichaModal}
        onClose={() => setShowFichaModal(false)}
        miembro={selectedMiembro}
        onChanged={() => loadMiembros(response.page)}
      />

      <SoftDeleteMiembroModal
        isOpen={showSoftDeleteModal}
        onClose={() => {
          setShowSoftDeleteModal(false);
          setSelectedMiembro(null);
        }}
        onConfirm={handleConfirmSoftDelete}
        miembro={selectedMiembro}
      />

      <HardDeleteMiembroModal
        isOpen={showHardDeleteModal}
        onClose={() => {
          setShowHardDeleteModal(false);
          setSelectedMiembro(null);
        }}
        onConfirm={handleConfirmHardDelete}
        miembro={selectedMiembro}
      />

      <MiembrosSinDerechosModal
        isOpen={showSinDerechosModal}
        onClose={() => setShowSinDerechosModal(false)}
        onMiembroReingresado={() => loadMiembros(response.page)}
      />
    </div>
  );
};
